import { useState, useRef, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Modal } from '@proaxiv/ui'
import {
  useListMembersQuery,
  useInviteOrgMemberMutation,
  useResendOrgMemberInvitationMutation,
  useRemoveMemberMutation,
} from '@proaxiv/server-api'
import type { ListMembersResponse, MemberSummary, ApiError } from '@proaxiv/server-api'
import { Button } from './Button'
import { ConfirmationModal } from './ConfirmationModal'
import { formatEmail } from '../utils/email'
import { formatFullName } from '../utils/participantMappers'

// ── Icons ────────────────────────────────────────────────────────────────────

const MembersIcon = ({ size = 16 }: { size?: number }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.75"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
    <circle cx="9" cy="7" r="4" />
    <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
    <path d="M16 3.13a4 4 0 0 1 0 7.75" />
  </svg>
)

const UserPlusIcon = ({ size = 14 }: { size?: number }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
    <circle cx="8.5" cy="7" r="4" />
    <line x1="20" y1="8" x2="20" y2="14" />
    <line x1="23" y1="11" x2="17" y2="11" />
  </svg>
)

const TrashIcon = ({ size = 12 }: { size?: number }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <polyline points="3 6 5 6 21 6" />
    <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
    <path d="M10 11v6M14 11v6" />
    <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
  </svg>
)

const RefreshIcon = ({ size = 12 }: { size?: number }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <polyline points="23 4 23 10 17 10" />
    <polyline points="1 20 1 14 7 14" />
    <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
  </svg>
)

const CheckIcon = ({ size = 12 }: { size?: number }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <polyline points="20 6 9 17 4 12" />
  </svg>
)

const XIcon = ({ size = 16 }: { size?: number }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
)

const ShieldIcon = ({ size = 12 }: { size?: number }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
  </svg>
)

const PersonIcon = ({ size = 12 }: { size?: number }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
    <circle cx="12" cy="7" r="4" />
  </svg>
)

// ── Formatting helpers ──────────────────────────────────────────────────────

function formatLastSent(
  iso: string | undefined,
  t: (key: string) => string,
  locale: string
): string {
  if (!iso) return t('org_detail_last_sent_never')
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return t('org_detail_last_sent_never')
  return date.toLocaleString(locale, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

// ── RoleBadge ────────────────────────────────────────────────────────────────

function RoleBadge({ role }: { role: string }) {
  if (role === 'OWNER') {
    return (
      <span className="bg-pax-purple inline-flex items-center gap-1 rounded-md px-2 py-0.5 text-xs font-medium text-white">
        <ShieldIcon />
        Owner
      </span>
    )
  }
  if (role === 'ADMIN') {
    return (
      <span className="bg-pax-blue inline-flex items-center gap-1 rounded-md px-2 py-0.5 text-xs font-medium text-white">
        <ShieldIcon />
        Admin
      </span>
    )
  }
  return (
    <span className="border-pax-blue/30 text-pax-blue inline-flex items-center gap-1 rounded-md border bg-white px-2 py-0.5 text-xs font-medium">
      <PersonIcon />
      Member
    </span>
  )
}

// ── InviteModal ───────────────────────────────────────────────────────────────

interface InviteForm {
  email: string
}

interface InviteModalProps {
  onClose: () => void
  onSend: (form: InviteForm) => void
  isSending: boolean
  error: string | null
}

function InviteModal({ onClose, onSend, isSending, error }: InviteModalProps) {
  const { t } = useTranslation()
  const [form, setForm] = useState<InviteForm>({ email: '' })

  function handleChange(field: keyof InviteForm, value: string) {
    setForm((prev) => ({ ...prev, [field]: value }))
  }

  function handleSend() {
    if (form.email.trim() && !isSending) {
      onSend(form)
    }
  }

  return (
    <Modal backdrop="light" onBackdropClick={isSending ? undefined : onClose}>
      <div className="border-pax-purple/50 flex w-full max-w-sm flex-col gap-5 rounded-2xl border bg-white p-6 shadow-xl ring-1 ring-black/5">
        <button
          type="button"
          onClick={onClose}
          className="text-pax-gray hover:text-pax-black absolute top-4 right-4 cursor-pointer transition-colors focus-visible:outline-none"
          aria-label="Close"
        >
          <XIcon />
        </button>

        <div className="flex flex-col gap-1 pr-6">
          <div className="flex items-center gap-2">
            <div className="bg-pax-light-blue text-pax-blue flex h-7 w-7 items-center justify-center rounded-lg">
              <UserPlusIcon size={13} />
            </div>
            <h2 className="text-pax-black text-base font-bold">{t('org_detail_invite_title')}</h2>
          </div>
          <p className="text-pax-gray text-xs">{t('org_detail_invite_subtitle')}</p>
        </div>

        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-1.5">
            <label htmlFor="invite-email" className="text-pax-black text-sm font-medium">
              {t('reg_form_email')}
            </label>
            <input
              id="invite-email"
              type="email"
              value={form.email}
              onChange={(e) => handleChange('email', e.target.value)}
              className="border-pax-purple/30 text-pax-black placeholder:text-pax-gray focus:ring-pax-blue/30 focus:border-pax-blue rounded-lg border px-3 py-2 text-sm transition-all focus:ring-2 focus:outline-none"
            />
            <p className="text-pax-gray text-xs">{t('org_detail_invite_email_hint')}</p>
          </div>
        </div>

        {error && (
          <div className="bg-pax-red/8 border-pax-red/20 flex items-start gap-2 rounded-lg border px-3 py-2.5">
            <svg
              className="text-pax-red mt-0.5 h-3.5 w-3.5 shrink-0"
              viewBox="0 0 24 24"
              fill="currentColor"
            >
              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z" />
            </svg>
            <p className="text-pax-red text-xs">{error}</p>
          </div>
        )}

        <div className="flex justify-end gap-3">
          <Button variant="secondary" size="sm" onClick={onClose} disabled={isSending}>
            {t('btn_cancel')}
          </Button>
          {isSending ? (
            <div className="bg-pax-blue inline-flex h-[30px] w-[30px] items-center justify-center rounded-lg">
              <svg
                className="h-4 w-4 animate-spin text-white"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                aria-label="Sending"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8v4l3-3-3-3v4a8 8 0 00-8 8h4z"
                />
              </svg>
            </div>
          ) : (
            <Button variant="primary" size="sm" onClick={handleSend} disabled={!form.email.trim()}>
              {t('org_detail_invite_send')}
            </Button>
          )}
        </div>
      </div>
    </Modal>
  )
}

// ── OrgMembersPanel ───────────────────────────────────────────────────────────

type MemberSubTab = 'members' | 'pending'

interface OrgMembersPanelProps {
  orgId: string
  isOwner: boolean
}

/**
 * Reusable members panel — shows the active-members table and pending-invitations
 * table with invite / resend / remove actions gated on `isOwner`.
 *
 * Used by both `OrganizationDetailPage` (inside the Members tab) and
 * `OrgMembersPage` (standalone `/users` route).
 */
export function OrgMembersPanel({ orgId, isOwner }: OrgMembersPanelProps) {
  const { t, i18n } = useTranslation()

  const { data: rawMembers } = useListMembersQuery({ orgId }, { skip: !orgId })
  const allMembers: MemberSummary[] = (rawMembers as ListMembersResponse | undefined)?.members ?? []
  const activeMembers = allMembers.filter((m) => m.status === 'ACTIVE')
  const pendingMembers = allMembers.filter((m) => m.status === 'PENDING')

  const [inviteOrgMember, { isLoading: isInviting }] = useInviteOrgMemberMutation()
  const [resendOrgMemberInvitation] = useResendOrgMemberInvitationMutation()
  const [removeMember] = useRemoveMemberMutation()

  const [memberSubTab, setMemberSubTab] = useState<MemberSubTab>('members')
  const [showInviteModal, setShowInviteModal] = useState(false)
  const [inviteError, setInviteError] = useState<string | null>(null)
  const [memberToRemove, setMemberToRemove] = useState<string | null>(null)
  const [invitationToDelete, setInvitationToDelete] = useState<string | null>(null)
  const [sentInvitationId, setSentInvitationId] = useState<string | null>(null)
  const [invitedEmail, setInvitedEmail] = useState<string | null>(null)
  const sentResetTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const invitedResetTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    return () => {
      if (sentResetTimerRef.current) clearTimeout(sentResetTimerRef.current)
      if (invitedResetTimerRef.current) clearTimeout(invitedResetTimerRef.current)
    }
  }, [])

  function openInviteModal() {
    setInviteError(null)
    setShowInviteModal(true)
  }

  function closeInviteModal() {
    setInviteError(null)
    setShowInviteModal(false)
  }

  async function handleSendInvitation(form: InviteForm) {
    setInviteError(null)
    // Normalized so a casing/full-width variant of an already-pending invite is rejected as
    // the duplicate it is, rather than silently creating a second invitation (see #175).
    const email = form.email.trim() ? formatEmail(form.email) : undefined
    const result = await inviteOrgMember({
      orgId,
      inviteBody: {
        email,
      },
    })
    if ('error' in result) {
      const err = result.error
      let message = 'Failed to send invitation. Please try again.'
      if (err && 'data' in err && err.data && typeof err.data === 'object') {
        const apiError = err.data as ApiError
        if (apiError.message) message = apiError.message
      }
      setInviteError(message)
    } else {
      setShowInviteModal(false)
      setMemberSubTab('pending')
      setInvitedEmail(form.email)
      if (invitedResetTimerRef.current) clearTimeout(invitedResetTimerRef.current)
      invitedResetTimerRef.current = setTimeout(() => {
        setInvitedEmail(null)
      }, 5000)
    }
  }

  function requestRemoveMember(memberId: string) {
    setMemberToRemove(memberId)
  }

  function handleConfirmRemoveMember(result: { notify: boolean }) {
    if (memberToRemove) {
      void removeMember({
        orgId,
        memberId: memberToRemove,
        removeMemberBody: { sendEmailNotification: result.notify },
      })
    }
    setMemberToRemove(null)
  }

  function requestDeleteInvitation(invitationId: string) {
    setInvitationToDelete(invitationId)
  }

  function handleConfirmDeleteInvitation() {
    if (invitationToDelete) {
      void removeMember({
        orgId,
        memberId: invitationToDelete,
        removeMemberBody: { sendEmailNotification: false },
      })
    }
    setInvitationToDelete(null)
  }

  async function handleResendInvitation(invitationId: string) {
    const result = await resendOrgMemberInvitation({ orgId, invitationId })
    if (!('error' in result)) {
      setSentInvitationId(invitationId)
      if (sentResetTimerRef.current) clearTimeout(sentResetTimerRef.current)
      sentResetTimerRef.current = setTimeout(() => {
        setSentInvitationId((current) => (current === invitationId ? null : current))
      }, 1500)
    }
  }

  return (
    <>
      <div className="border-pax-purple/20 flex flex-col gap-5 rounded-2xl border bg-white p-5">
        {/* Header — title and button share a row on desktop; on narrow mobile widths the title
            + "Invite member" button no longer both fit on one line, which used to force
            "Organization Members" to wrap mid-row next to the button and overflow it into a
            cramped two-line title. Stacking them (button on its own full-width row below the
            title) avoids that squeeze entirely. The subtitle always gets its own full-width row
            regardless. */}
        <div className="flex flex-col gap-3 sm:gap-1.5">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between sm:gap-4">
            <div className="flex items-center gap-2">
              <MembersIcon size={16} />
              <h3 className="text-pax-black text-sm font-bold">{t('org_detail_members_title')}</h3>
            </div>
            {isOwner && (
              <Button
                variant="primary"
                size="sm"
                leftIcon={<UserPlusIcon size={13} />}
                onClick={openInviteModal}
                className="w-full sm:w-auto"
              >
                {t('org_detail_invite_btn')}
              </Button>
            )}
          </div>
          <p className="text-pax-gray w-full text-xs">{t('org_detail_members_subtitle')}</p>
        </div>

        {invitedEmail && (
          <div
            role="status"
            className="bg-pax-green/8 border-pax-green/25 flex items-center gap-2 rounded-lg border px-3 py-2.5"
          >
            <span className="bg-pax-green flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-white">
              <CheckIcon size={11} />
            </span>
            <p className="text-pax-black text-xs font-medium">
              {t('org_detail_invite_success', { email: invitedEmail })}
            </p>
          </div>
        )}

        {/* Sub-tabs */}
        <div className="border-pax-purple/20 flex overflow-hidden rounded-lg border">
          <button
            type="button"
            onClick={() => setMemberSubTab('members')}
            className={[
              'flex-1 cursor-pointer py-2 text-xs font-semibold transition-colors focus-visible:outline-none',
              memberSubTab === 'members'
                ? 'bg-pax-light-purple text-pax-black'
                : 'text-pax-gray hover:bg-pax-light-purple/50 bg-white',
            ].join(' ')}
          >
            {t('org_detail_members_tab', { count: activeMembers.length })}
          </button>
          <button
            type="button"
            data-testid="pending-invitations-tab"
            onClick={() => setMemberSubTab('pending')}
            className={[
              'border-pax-purple/20 flex-1 cursor-pointer border-l py-2 text-xs font-semibold transition-colors focus-visible:outline-none',
              memberSubTab === 'pending'
                ? 'bg-pax-light-purple text-pax-black'
                : 'text-pax-gray hover:bg-pax-light-purple/50 bg-white',
            ].join(' ')}
          >
            {t('org_detail_pending_tab', { count: pendingMembers.length })}
          </button>
        </div>

        {/* Active members table */}
        {memberSubTab === 'members' && (
          <div className="flex flex-col">
            {/* Mobile card list */}
            <div data-testid="mobile-view" className="flex flex-col gap-3 p-3 md:hidden">
              {activeMembers.length === 0 ? (
                <p className="text-pax-gray py-6 text-center text-sm">
                  {t('org_detail_no_members')}
                </p>
              ) : (
                activeMembers.map((member) => {
                  const displayName =
                    formatFullName(member.firstName, member.lastName) || member.email
                  return (
                    <div
                      key={member.memberId}
                      className="border-pax-purple/20 rounded-2xl border bg-white p-4 shadow-sm"
                    >
                      <div className="mb-3">
                        <p className="text-pax-black line-clamp-2 font-medium break-all">
                          {displayName}
                        </p>
                        <p className="text-pax-gray line-clamp-2 text-xs break-all">
                          {member.email}
                        </p>
                        <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
                          <RoleBadge role={member.role} />
                        </div>
                      </div>
                      {isOwner && member.role !== 'OWNER' && (
                        <Button
                          variant="unstyled"
                          onClick={() => requestRemoveMember(member.memberId)}
                          leftIcon={<TrashIcon />}
                          className="flex w-full items-center justify-center gap-1.5 rounded-xl px-3 py-2 text-sm font-medium text-white transition-opacity hover:opacity-85 focus-visible:outline-none"
                          style={{
                            background: 'linear-gradient(135deg, #FF5B37 0%, #f2a529 100%)',
                          }}
                        >
                          {t('org_detail_remove_btn')}
                        </Button>
                      )}
                    </div>
                  )
                })
              )}
            </div>

            {/* Desktop grid */}
            <div data-testid="desktop-view" className="hidden md:block">
              <div className="border-pax-purple/15 grid grid-cols-[1fr_200px_120px] gap-4 border-b px-3 pb-2">
                <span className="text-pax-gray text-xs font-semibold tracking-wide uppercase">
                  {t('org_detail_col_name')}
                </span>
                <span className="text-pax-gray text-xs font-semibold tracking-wide uppercase">
                  {t('org_detail_col_status')}
                </span>
                <span className="text-pax-gray text-right text-xs font-semibold tracking-wide uppercase">
                  {t('reg_col_actions')}
                </span>
              </div>
              {activeMembers.length === 0 ? (
                <p className="text-pax-gray py-6 text-center text-sm">
                  {t('org_detail_no_members')}
                </p>
              ) : (
                activeMembers.map((member) => {
                  const displayName =
                    formatFullName(member.firstName, member.lastName) || member.email
                  return (
                    <div
                      key={member.memberId}
                      className="border-pax-purple/10 hover:bg-pax-light-purple/30 grid grid-cols-[1fr_200px_120px] items-center gap-4 border-b px-3 py-3 transition-colors last:border-0"
                    >
                      <div className="flex min-w-0 flex-col">
                        <span className="text-pax-black line-clamp-2 text-sm font-medium break-all">
                          {displayName}
                        </span>
                        <span className="text-pax-gray line-clamp-2 text-xs break-all">
                          {member.email}
                        </span>
                      </div>
                      <div className="flex flex-wrap items-center gap-1.5">
                        <RoleBadge role={member.role} />
                      </div>
                      <div className="flex justify-end">
                        {isOwner && member.role !== 'OWNER' && (
                          <Button
                            variant="unstyled"
                            onClick={() => requestRemoveMember(member.memberId)}
                            leftIcon={<TrashIcon />}
                            className="inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-medium text-white transition-opacity hover:opacity-85 focus-visible:outline-none"
                            style={{
                              background: 'linear-gradient(135deg, #FF5B37 0%, #f2a529 100%)',
                            }}
                          >
                            {t('org_detail_remove_btn')}
                          </Button>
                        )}
                      </div>
                    </div>
                  )
                })
              )}
            </div>
          </div>
        )}

        {/* Pending invitations table */}
        {memberSubTab === 'pending' && (
          <div className="flex flex-col" data-testid="pending-invitations-panel">
            {/* Mobile card list */}
            <div data-testid="mobile-view" className="flex flex-col gap-3 p-3 md:hidden">
              {pendingMembers.length === 0 ? (
                <p className="text-pax-gray py-6 text-center text-sm">
                  {t('org_detail_no_pending')}
                </p>
              ) : (
                pendingMembers.map((inv) => (
                  <div
                    key={inv.memberId}
                    className="border-pax-purple/20 rounded-2xl border bg-white p-4 shadow-sm"
                  >
                    <div className="mb-3">
                      {/* A long, unbroken email address (no spaces to wrap on) would otherwise
                          overflow the card's width instead of wrapping — `break-all` lets it
                          wrap mid-string, and `line-clamp-2` caps it at two lines with an
                          ellipsis so one pathological address can't blow out the card's height. */}
                      <p className="text-pax-black line-clamp-2 font-medium break-all">
                        {inv.email}
                      </p>
                      <p className="text-pax-gray text-xs">
                        {t('org_detail_col_last_sent')}:{' '}
                        {formatLastSent(inv.invitedAt, t, i18n.language)}
                      </p>
                    </div>
                    {isOwner && (
                      <div className="flex items-center gap-2">
                        {sentInvitationId === inv.memberId ? (
                          <Button
                            variant="unstyled"
                            disabled
                            leftIcon={<CheckIcon />}
                            // Matches the Delete button's `size="sm"` padding (px-3 py-1, text-xs)
                            // so the two buttons in this row are the same height — this used to
                            // be the larger px-3/py-2/text-sm combo, making Resend visibly taller.
                            className="bg-pax-green flex min-w-0 flex-1 items-center justify-center gap-1.5 rounded-lg px-3 py-1 text-xs font-medium text-white"
                          >
                            {t('org_detail_resend_sent_btn')}
                          </Button>
                        ) : (
                          <Button
                            variant="unstyled"
                            onClick={() => void handleResendInvitation(inv.memberId)}
                            leftIcon={<RefreshIcon />}
                            className="flex min-w-0 flex-1 items-center justify-center gap-1.5 rounded-lg px-3 py-1 text-xs font-medium text-white transition-opacity hover:opacity-85 focus-visible:outline-none"
                            style={{
                              background: 'linear-gradient(135deg, #f2a529 0%, #FF5B37 100%)',
                            }}
                          >
                            {t('org_detail_resend_btn')}
                          </Button>
                        )}
                        <Button
                          variant="danger"
                          size="sm"
                          onClick={() => requestDeleteInvitation(inv.memberId)}
                          leftIcon={<TrashIcon />}
                          // The default "danger" size (`md`, px-5) is wider than this card's
                          // available width once paired with the "Resend" button on narrow
                          // mobile screens — that mismatch pushed the row (and the whole card)
                          // past the viewport edge, causing horizontal overflow. `size="sm"`
                          // matches Resend's now-equally-compact padding; `min-w-0` lets the
                          // flex item shrink below its icon+text intrinsic width instead of
                          // forcing it.
                          className="min-w-0 flex-1"
                        >
                          {t('org_detail_delete_invitation_btn')}
                        </Button>
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>

            {/* Desktop grid */}
            <div data-testid="desktop-view" className="hidden md:block">
              <div className="border-pax-purple/15 grid grid-cols-[1fr_180px_180px] gap-4 border-b px-3 pb-2">
                <span className="text-pax-gray text-xs font-semibold tracking-wide uppercase">
                  {t('org_detail_col_name')}
                </span>
                <span className="text-pax-gray text-xs font-semibold tracking-wide uppercase">
                  {t('org_detail_col_last_sent')}
                </span>
                <span className="text-pax-gray text-right text-xs font-semibold tracking-wide uppercase">
                  {t('reg_col_actions')}
                </span>
              </div>
              {pendingMembers.length === 0 ? (
                <p className="text-pax-gray py-6 text-center text-sm">
                  {t('org_detail_no_pending')}
                </p>
              ) : (
                pendingMembers.map((inv) => (
                  <div
                    key={inv.memberId}
                    className="border-pax-purple/10 hover:bg-pax-light-purple/30 grid grid-cols-[1fr_180px_180px] items-center gap-4 border-b px-3 py-3 transition-colors last:border-0"
                  >
                    <div className="flex min-w-0 flex-col">
                      <span className="text-pax-black line-clamp-2 text-sm font-medium break-all">
                        {inv.email}
                      </span>
                    </div>
                    <span
                      className="text-pax-gray text-xs"
                      data-testid={`last-sent-${inv.memberId}`}
                    >
                      {formatLastSent(inv.invitedAt, t, i18n.language)}
                    </span>
                    <div className="flex justify-end gap-2">
                      {isOwner && (
                        <>
                          {sentInvitationId === inv.memberId ? (
                            <Button
                              variant="unstyled"
                              disabled
                              leftIcon={<CheckIcon />}
                              className="bg-pax-green inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-medium text-white"
                            >
                              {t('org_detail_resend_sent_btn')}
                            </Button>
                          ) : (
                            <Button
                              variant="unstyled"
                              onClick={() => void handleResendInvitation(inv.memberId)}
                              leftIcon={<RefreshIcon />}
                              className="inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-medium text-white transition-opacity hover:opacity-85 focus-visible:outline-none"
                              style={{
                                background: 'linear-gradient(135deg, #f2a529 0%, #FF5B37 100%)',
                              }}
                            >
                              {t('org_detail_resend_btn')}
                            </Button>
                          )}
                          <Button
                            variant="danger"
                            size="sm"
                            onClick={() => requestDeleteInvitation(inv.memberId)}
                            leftIcon={<TrashIcon />}
                            aria-label={t('org_detail_delete_invitation_btn')}
                          >
                            {t('org_detail_delete_invitation_btn')}
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        )}
      </div>

      {showInviteModal && (
        <InviteModal
          onClose={closeInviteModal}
          onSend={(form) => void handleSendInvitation(form)}
          isSending={isInviting}
          error={inviteError}
        />
      )}

      {memberToRemove && (
        <ConfirmationModal
          title={t('org_detail_remove_confirm')}
          notify={false}
          onConfirm={handleConfirmRemoveMember}
          onCancel={() => setMemberToRemove(null)}
        />
      )}

      {invitationToDelete && (
        <ConfirmationModal
          title={t('org_detail_delete_invitation_confirm')}
          notify={false}
          showNotifyCheckbox={false}
          onConfirm={handleConfirmDeleteInvitation}
          onCancel={() => setInvitationToDelete(null)}
        />
      )}
    </>
  )
}
